// Copyright (c) 2017 Deere & Company

import {isLengthAtLeast, isLengthWithinRange} from '../../../shared/services/validation-service';
import KeyValuePair from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import {hasAccess} from '../../../shared/services/security';

export const buildWipOpenPipCount = wipOpenPips => wipOpenPips.expiredCount + wipOpenPips.pinsExpiringInNext90Days + wipOpenPips.currentPinsCount;

export const mapWipClaimStatus = (key, wipClaims) => {

    const map = {
        '01': wipClaims.draft,
        1: wipClaims.newClaims,
        16: wipClaims.selectedForAudit,
        4: wipClaims.rejected,
        6: wipClaims.forwarded,
        7: wipClaims.returned,
        8: wipClaims.revised,
        9: wipClaims.waitingForDealerInvoice
    };

    return map[key];

};

export const isUserSelectedStatus = statusKey => ['01', '1', '4', '7', '8', '9', '16'].some(selectedStatusKey => selectedStatusKey === statusKey);

export const wipClaimStatus = [
    {
        key: '01',
        value: 'CLAIM_DRAFT'
    },
    {
        key: '1',
        value: 'NEW'
    },
    {
        key: '4',
        value: 'REJECTED'
    },
    {
        key: '6',
        value: 'FORWARDED'
    },
    {
        key: '7',
        value: 'RETURNED'
    },
    {
        key: '8',
        value: 'REVISED'
    },
    {
        key: '9',
        value: 'WAITING_FOR_DLR_INV'
    },
    {
        key: '16',
        value: 'SELECTED_FOR_AUDIT'
    }
];

export const isMatchingStatus = (statusKey, selectedStatusList) => selectedStatusList.find(selectedStatus => selectedStatus.key === statusKey);

/* eslint-disable complexity */

export const mapWipClaimColorCoding = (key, wipClaims) => {

    const map = {
        '01': '',
        1: '',
        16: wipClaims.selectedForAudit > 0 ?
            'dot-icon-red' :
            '',
        4: wipClaims.rejected > 0 ?
            'dot-icon-red' :
            '',
        6: '',
        7: wipClaims.returned > 0 ?
            'dot-icon-red' :
            '',
        8: '',
        9: wipClaims.waitingForDealerInvoice > 0 ?
            'dot-icon-red' :
            ''
    };

    return map[key];

};

/* eslint-enable complexity */

export const hasPendingOrRejectedClaims = wipClaim => wipClaim.rejected || wipClaim.returned || wipClaim.waitingForDealerInvoice || wipClaim.selectedForAudit;

export const getWipClaimIndicator = (wipClaim, total) => {

    let className;

    className = '';

    if (hasPendingOrRejectedClaims(wipClaim)) {

        className = 'dot-icon-red';

    } else if (total) {

        className = 'dot-icon-yellow';

    }

    return className;

};

export const getWipPipsIndicator = wipPips => {

    let className;

    className = '';

    if (wipPips.expiredCount) {

        className = 'dot-icon-red';

    } else if (wipPips.pinsExpiringInNext90Days) {

        className = 'dot-icon-yellow';

    }

    return className;

};

export const getNavigationLinks = (status, key) => {

    const map = {
        CLAIM_DRAFT: `#/claims/claims-in-progress?status=${key}`,
        CURRENT: '#/pips/open-pips',
        DEALER_RECOMMENDATIONS: '#/products/dealerrecommendations',
        EXPIRED: '#/pips/open-pips',
        EXPIRING_SOON: '#/pips/open-pips',
        FORWARDED: `#/claims/claims-in-progress?status=${key}`,
        NEW: `#/claims/claims-in-progress?status=${key}`,
        OPEN_PART_REQUESTS: '#/part-requests/open',
        REGISTRATIONS_REVIEW: '#/products/registrations',
        REJECTED: `#/claims/claims-in-progress?status=${key}`,
        RETURNED: `#/claims/claims-in-progress?status=${key}`,
        REVISED: `#/claims/claims-in-progress?status=${key}`,
        SELECTED_FOR_AUDIT: `#/claims/claims-in-progress?status=${key}`,
        TRANSFER_APPROVALS: '#/products/transfers',
        WAITING_FOR_DLR_INV: `#/claims/claims-in-progress?status=${key}`
    };

    return map[status];

};

const isQueryStringValid = query => query.length >= 4;

const validateWorkOrder = queryString => {

    const workOrderMinLength = 2;

    return isLengthAtLeast(queryString, workOrderMinLength);

};

const validatePin = queryString => {

    const pinMaxLength = 17;
    const pinMinLength = 6;

    return isLengthWithinRange(queryString, pinMaxLength, pinMinLength);

};

const validatePipProgram = queryString => {

    const pipMaxLength = 12;
    const pipMinLength = 4;

    return isLengthWithinRange(queryString, pipMaxLength, pipMinLength);

};

const validatePips = queryString => {

    const pinMaxLength = 17;
    const pinMinLength = 6;

    return isLengthWithinRange(queryString, pinMaxLength, pinMinLength);

};

export const mapClaimValidation = (key, queryString) => {

    const map = {
        CLAIMS: validateWorkOrder,
        PART_REQUESTS: isQueryStringValid,
        PIPS: validatePips,
        PIP_PROGRAMS: validatePipProgram,
        PRODUCTS: validatePin
    };

    return map[key](queryString);

};

export const mapSearchPlaceHolder = key => {

    const map = {
        CLAIMS: 'SEARCH_BY_WORK_ORDER',
        PART_REQUESTS: 'PART_REQUESTS_SEARCH_BY_CLAIM_PLACEHOLDER',
        PIPS: 'SEARCH_PRODUCT_BY_PIN_PLACEHOLDER',
        PIP_PROGRAMS: 'SEARCH_PRODUCT_BY_PIP_PLACEHOLDER',
        PRODUCTS: 'SEARCH_PRODUCT_BY_PIN_PLACEHOLDER'
    };

    const mapKey = map[key] || '';

    return mapKey;

};

export const mapValidationErrorMessages = key => {

    const map = {
        CLAIMS: 'SEARCH_BY_WORK_ORDER_VALIDATION_FAIL',
        PART_REQUESTS: 'PART_REQUESTS_SEARCH_BY_CLAIM_VALIDATION_ERROR',
        PIPS: 'SEARCH_PRODUCT_BY_PIN_VALIDATION_FAIL',
        PIP_PROGRAMS: 'SEARCH_PRODUCT_BY_PIP_VALIDATION_FAIL',
        PRODUCTS: 'SEARCH_PRODUCT_BY_PIN_VALIDATION_FAIL'
    };

    return map[key];

};

export const mapSearchRoutes = (key, query) => {

    const map = {
        CLAIMS: `claims/search?by=WORK_ORDER&q=${encodeURIComponent(query)}`,
        PART_REQUESTS: `part-requests/search?by=CLAIM&q=${encodeURIComponent(query)}`,
        PIPS: `pips/search?by=PIN&q=${encodeURIComponent(query)}`,
        PIP_PROGRAMS: `pips/search?by=PIP&q=${encodeURIComponent(query)}`,
        PRODUCTS: `products/search?by=PIN&q=${encodeURIComponent(query)}`
    };

    return map[key];

};

/* eslint-disable complexity */

export const getSearchBy = security => {

    const searchBy = [];

    if (hasAccess(security, 'PRT', 'PRT_PRODUCT_SEARCH')) {

        searchBy.push(new KeyValuePair({
            key: 'PRODUCTS',
            value: 'PRODUCTS'
        }));

    }

    if (hasAccess(security, 'PART_RETURNS_EDIT')) {

        searchBy.push(new KeyValuePair({
            key: 'PART_REQUESTS',
            value: 'PART_REQUESTS'
        }));

    }

    if (hasAccess(security, 'CLAIM')) {

        searchBy.push(new KeyValuePair({
            key: 'CLAIMS',
            value: 'CLAIMS'
        }));

    }

    if (hasAccess(security, 'PIP')) {

        searchBy.push(new KeyValuePair({
            key: 'PIPS',
            value: 'PIPS'
        }));

        searchBy.push(new KeyValuePair({
            key: 'PIP_PROGRAMS',
            value: 'PIP_PROGRAMS'
        }));

    }

    return searchBy;

};

/* eslint-enable complexity */

export const getSearchBySelectedOption = searchBy =>
    searchBy.length > 0 ? searchBy[0].key : '';

/* eslint-enable complexity */

export const isCloudPrefferred = entitlements => entitlements.some(service => {

    if (service.key === 'transfers' && service.value === 'true') {

        return true;

    }
    return false;

});

/* eslint-disable complexity, max-params */

export const buildTransferPayload = (selectedAccounts, exportToExcel, language, layout = '', listId = '') => ({
    accounts: selectedAccounts.map(selectedAccount => selectedAccount.account),
    export: exportToExcel,
    language,
    layout,
    listId,
    requestedDateFrom: null,
    requestedDateTo: null
});

/* eslint-enable complexity, max-params */



// WEBPACK FOOTER //
// ./src/home-page/shared/services/home-page-helper.js